import React, { lazy, Suspense, useRef, useState, useEffect, useMemo } from "react";
import { Row, Col, Container, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet";
import CacheBuster from "react-cache-buster";
import useSharedEquityLoanData from "./Utils/useSharedEquityLoanData.js";
import Banner from "../DSCRHELOC/Banner/Banner.js";
import { useLocation } from "react-router-dom";

const ClientFlexibility = lazy(() => import("./ClientFlexibility/Client.js"));
const WhatIsCribEquity = lazy(() => import("./whatIsCribEquity/index.js"));
const HowItWorks = lazy(() => import("./HowItWorks/index.js"));
const AboutSection = lazy(() => import("../AboutSection/index.js"));
const FooterDisclaimer = lazy(() =>
  import("../../components/FooterDisclaimer/FooterDisclaimer")
);

function LoHome({ brokerData, brokerUrl }) {
  const data = useSharedEquityLoanData();
  
  // You'll need to define these variables or import them
  const appVersion = process.env.REACT_APP_VERSION || "1.0.0";


  const [partnerOfficer, setPartnerOfficer] = useState();
  const [showOtherComponents, setShowOtherComponents] = useState(false);


  const productSectionRef = useRef(null);
  // Check if the location state has scrollToProductSection
  const location = useLocation();

  // Function to extract username from URL (similar to your original getUserNameFromURL)
  const getUserNameFromURL = () => {
    try {
      const pathSegments = window.location.pathname.split('/').filter(segment => segment);
      // Assuming URL structure is /partner/{username}/
      const partnerIndex = pathSegments.indexOf('partner');
      if (partnerIndex !== -1 && pathSegments[partnerIndex + 1]) {
        return pathSegments[partnerIndex + 1];
      }
      return null;
    } catch (error) {
      console.error('Error extracting username from URL:', error);
      return null;
    }
  };

  // Create canonical URL using useMemo for performance
  const canonicalUrl = useMemo(() => {
    try {
      const userName = getUserNameFromURL();
      
      if (!userName) {
        console.error('Invalid username in URL');
        return null;
      }

      // Clean and validate the username
      const cleanUserName = encodeURIComponent(userName.trim());
      
      // Construct the canonical URL
      const protocol = 'https://';
      const domain = 'www.angelai.com';
      const basePath = '/partner/';
      const canonicalUrl = `${protocol}${domain}${basePath}${cleanUserName}/dscr`;

      // Validate the constructed URL
      try {
        new URL(canonicalUrl);
        return canonicalUrl;
      } catch (e) {
        console.error('Invalid URL constructed:', canonicalUrl);
        return null;
      }
    } catch (error) {
      console.error('Error creating canonical URL:', error);
      return null;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (location.state?.scrollToProductSection) {
      productSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  

  // Scroll effect - kept separate as it's not data fetching
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const scrollPosition =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      const maxScroll = documentHeight - windowHeight;
      const percentage = (scrollPosition / maxScroll) * 100;

      if (percentage > 50) {
        setShowOtherComponents(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Clean up the scroll listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

   // Scroll effect - kept separate as it's not data fetching
   useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const scrollPosition =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      const maxScroll = documentHeight - windowHeight;
      const percentage = (scrollPosition / maxScroll) * 100;

      if (percentage > 50) {
        setShowOtherComponents(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    // Clean up the scroll listener when the component unmounts
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  return (
    <CacheBuster
      currentVersion={appVersion}
      isEnabled={true}
      isVerboseMode={false}
      metaFileDirectory={process.env.PUBLIC_URL}
    >
      <Helmet>
        <title>
          {brokerData?.name 
            ? `DSCR HELOC for Real Estate Investors | ${brokerData.name}` 
            : "DSCR HELOC for Real Estate Investors"
          }
        </title>
        <meta
          name="description"
          content={`Discover flexible DSCR HELOC loan options tailored for property investors. Leverage your rental income to secure a revolving line of credit up to $3 Million.`}
        />
        {/* Canonical URL */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        
        <meta property="og:url" content={canonicalUrl || window.location.href} />
        <meta 
          property="og:title" 
          content={brokerData?.name 
            ? `DSCR HELOC for Real Estate Investors | ${brokerData.name}` 
            : "DSCR HELOC for Real Estate Investors"
          } 
        />
        <meta 
          property="og:description" 
          content={`Discover flexible DSCR HELOC loan options tailored for property investors. Leverage your rental income to secure a revolving line of credit up to $3 Million.`} 
        />
        <meta 
          property="og:keywords" 
          content="HELOC for Rental Property, HELOC for Investment Properties, DSCR HELOC, Debt Service Coverage Ratio, Home Equity Line of Credit, Rental property financing, investment property loans, real estate investor financing, Non-QM loans, rental income leverage, property investor credit line, flexible investment funding, real estate portfolio expansion, DSCR HELOC with Rick Martinez, real estate investor loans, investment property credit line, rental income leverage, property investor financing, flexible investment funding, real estate portfolio growth" 
        />
        <meta name="twitter:url" content={canonicalUrl || window.location.href} />
        <meta 
          name="twitter:title" 
          content={brokerData?.name 
            ? `DSCR HELOC for Real Estate Investors | ${brokerData.name}` 
            : "DSCR HELOC for Real Estate Investors"
          } 
        />
        <meta 
          name="twitter:description" 
          content={`Discover flexible DSCR HELOC loan options tailored for property investors. Leverage your rental income to secure a revolving line of credit up to $3 Million.`}  
        />
      </Helmet>
      
      <div className="">
        <Banner fetchpriority="high" data={data} />

        <Suspense fallback={<div />}>
          <WhatIsCribEquity data={data} />
        </Suspense>
        <Suspense fallback={<div />}>
          <ClientFlexibility data={data} />
        </Suspense>
        {showOtherComponents && (
              <>
        <Suspense fallback={<div />}>
          <HowItWorks data={data} />
        </Suspense>
        <Suspense fallback={<div />}>
          <AboutSection brokerData={brokerData} />
        </Suspense>
        <Suspense
          fallback={
            <div>
              <Spinner />
            </div>
          }
        >
          <FooterDisclaimer brokerData={brokerData} brokerUrl={brokerUrl} />
        </Suspense>
        </>
            )}
      </div>
    </CacheBuster>
  );
}

export default LoHome;